import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { provideStoreDevtools } from '@ngrx/store-devtools';
import { provideStore } from '@ngrx/store';
import { provideEnvironmentNgxMask } from 'ngx-mask';
import { FormGroupDirective } from '@angular/forms';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import {
  ErrorHandler,
  LOCALE_ID,
  DEFAULT_CURRENCY_CODE,
  isDevMode,
  importProvidersFrom,
  ApplicationConfig,
} from '@angular/core';
import { CustomLoader } from './translate.loader';
import { provideAnimations } from '@angular/platform-browser/animations';
import {
  provideRouter,
  Route,
  Routes,
  TitleStrategy,
  withComponentInputBinding,
  withViewTransitions
} from '@angular/router';

import { ErrorHandlerService } from '@application/errors/data-access';
import { AppConfig, AppConfigProvider, devExtremeKey } from '@application/config';
import { BreadcrumbTitleStrategy } from '@shared/components/breadcrumb/data-access';
import config from 'devextreme/core/config';
import { provideLottieOptions } from 'ngx-lottie';
import {
  LanguageInterceptor,
  LoadingBarInterceptor,
  TokenInterceptor,
  DateInterceptor,
} from '@application/http-client';
import { LoadingBarHttpClientModule } from '@ngx-loading-bar/http-client';
import { LoggerConfig } from '@shared/utils';
import ptBr from '@angular/common/locales/pt';
import enUs from '@angular/common/locales/en';
import esEs from '@angular/common/locales/es';
import deDe from '@angular/common/locales/de';

import { registerLocaleData } from '@angular/common';
// Register only the default locale initially - others can be loaded on demand
registerLocaleData(ptBr);
// Lazy load other locales when needed
// registerLocaleData(enUs);
// registerLocaleData(esEs);
// registerLocaleData(deDe);

config({ licenseKey: devExtremeKey });

export function LoaderFactory() {
  return new CustomLoader();
}

export type AppConfigOptions = {
  useLottie?: boolean;
}

export const appConfig = (environment: AppConfig, ProvideRouter: Routes | Route[], optionalConfig?: AppConfigOptions ): ApplicationConfig => {
  LoggerConfig.init();
  const configProvider = {
    providers: [
      provideHttpClient(
        withInterceptors([LanguageInterceptor, TokenInterceptor, LoadingBarInterceptor, DateInterceptor])
      ),
      AppConfigProvider(environment),
      provideStore(),
      provideRouter(
        ProvideRouter,
        withComponentInputBinding(),
        withViewTransitions()
      ),
      {
        provide: TitleStrategy,
        useClass: BreadcrumbTitleStrategy,
      },

      provideAnimations(),
      provideStoreDevtools({
        maxAge: 25,
        logOnly: !isDevMode(),
        autoPause: true,
        trace: false,
        traceLimit: 75,
        connectInZone: true,
      }),
      importProvidersFrom(LoadingBarHttpClientModule),
      importProvidersFrom(
        TranslateModule.forRoot({
          isolate: true,
          defaultLanguage: 'en-US',
          loader: {
            provide: TranslateLoader,
            useFactory: LoaderFactory,
          },
        })
      ),
      {
        provide: ErrorHandler,
        useClass: ErrorHandlerService,
      },
      {
        provide: LOCALE_ID,
        useValue: 'pt-BR',
      },
      {
        provide: DEFAULT_CURRENCY_CODE,
        useValue: 'BRL',
      },
      FormGroupDirective,
      provideEnvironmentNgxMask()
      // QuillConfigModule moved to lazy loading - only load when Quill components are used
      // importProvidersFrom(
      //   QuillConfigModule.forRoot({})
      // )
    ],
  };

  if (optionalConfig?.useLottie) {
    const playerFactory = () => import('lottie-web');
    configProvider.providers.push(provideLottieOptions({
      player: playerFactory,
    }));
  }

  return configProvider;
};
